import { entityService } from '@/services/EntityService';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildDxReportHtml,
  type DxReportItem,
  type DxReportTemplateOptions,
} from '@/reports/templates/dxReportTemplate';

interface TransactionLogAsset {
  assetId: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  transQty: number;
}

interface TransactionLogDetail {
  id: string;
  transactionType: string;
  memberName: string;
  memberId: string;
  dateProcessed: string;
  sourceWorkflow?: string;
  returnDisposition?: 'recoverable' | 'non_recoverable';
  assets: TransactionLogAsset[];
}

interface TransactionLogResponse {
  transactions: TransactionLogDetail[];
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const normalizeDateOnly = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const toTitle = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s]+/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const isExchangeWorkflow = (transaction: TransactionLogDetail): boolean => {
  const type = transaction.transactionType?.toUpperCase?.() ?? '';
  if (type === 'EXCHANGE') return true;
  const source = transaction.sourceWorkflow?.toLowerCase() ?? '';
  return source.includes('exchange') || source.includes('dx');
};

const formatTransactionDirection = (transaction: TransactionLogDetail): string => {
  const type = transaction.transactionType?.toUpperCase?.() ?? '';
  if (type === 'TURN_IN') return 'Turn In';
  if (type === 'ISSUE_OUT') return 'Issue';
  if (type === 'EXCHANGE') return 'DX';
  return type ? toTitle(type) : '—';
};

const coerceString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const parseAssetSize = (entityData: Record<string, unknown>): string | null => {
  const candidates = [
    'size',
    'sizeLabel',
    'uniform_size',
    'boot_size',
    'glove_size',
    'helmet_size',
  ];
  for (const key of candidates) {
    const value = coerceString(entityData[key]);
    if (value) return value;
  }
  return null;
};

const parseAssetCondition = (entityData: Record<string, unknown>): string | null => {
  const candidates = [
    'condition_code',
    'conditionCode',
    'condition',
    'service_type',
    'serviceType',
  ];
  for (const key of candidates) {
    const value = coerceString(entityData[key]);
    if (value) return value;
  }
  return null;
};

export const runDxReport = async (params: Record<string, unknown>): Promise<void> => {
  const fromDateRaw = params.fromDate;
  const toDateRaw = params.toDate;

  const fromDate = normalizeDateOnly(fromDateRaw);
  const toDate = normalizeDateOnly(toDateRaw);

  const query = new URLSearchParams({ limit: '2000', types: 'TURN_IN,ISSUE_OUT,EXCHANGE' });
  if (fromDate) query.set('from', fromDate);
  if (toDate) query.set('to', toDate);

  const response = await httpClient.get<TransactionLogResponse>(
    `/api/v1/transactions/log?${query.toString()}&source=report`,
  );
  const transactions = response.transactions ?? [];

  const exchangeTransactions = transactions.filter(isExchangeWorkflow);

  const assetIds = Array.from(
    new Set(
      exchangeTransactions
        .flatMap((transaction) => transaction.assets ?? [])
        .map((asset) => asset.assetId)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const assetEntityDataById = new Map<string, Record<string, unknown>>();

  const batches = chunk(assetIds, 75);
  for (const batchIds of batches) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const assets = await entityService.list({
      type: 'asset',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const asset of assets) {
      assetEntityDataById.set(asset.id, (asset.entity_data ?? {}) as Record<string, unknown>);
    }
  }

  // Collect catalog IDs referenced by assets and fetch catalog entity_data
  // Size and condition are stored on the catalog entity, not the asset.
  const catalogIds = Array.from(
    new Set(
      [...assetEntityDataById.values()]
        .map((data) => data.catalog_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const catalogEntityDataById = new Map<string, Record<string, unknown>>();

  const catalogBatches = chunk(catalogIds, 75);
  for (const batchIds of catalogBatches) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      catalogEntityDataById.set(catalog.id, (catalog.entity_data ?? {}) as Record<string, unknown>);
    }
  }

  // Build lookup: prefer catalog entity_data for size/condition, fall back to asset
  const assetDetailsById = new Map<string, { size: string | null; condition: string | null }>();

  for (const [assetId, assetData] of assetEntityDataById) {
    const catalogId = typeof assetData.catalog_id === 'string' ? assetData.catalog_id : null;
    const catalogData = catalogId ? catalogEntityDataById.get(catalogId) : undefined;

    const size = (catalogData ? parseAssetSize(catalogData) : null) ?? parseAssetSize(assetData);
    const condition =
      (catalogData ? parseAssetCondition(catalogData) : null) ?? parseAssetCondition(assetData);

    assetDetailsById.set(assetId, { size, condition });
  }

  const items: DxReportItem[] = exchangeTransactions.flatMap((transaction) => {
    const baseDirection = formatTransactionDirection(transaction);
    const isExchange = transaction.transactionType?.toUpperCase?.() === 'EXCHANGE';
    const date = transaction.dateProcessed ? (transaction.dateProcessed.split('T')[0] ?? '') : '';
    const individual = transaction.memberName ?? null;

    // Derive turn-in condition from exchange disposition
    const turnInCondition =
      transaction.returnDisposition === 'recoverable'
        ? 'S'
        : transaction.returnDisposition === 'non_recoverable'
          ? 'NS'
          : null;

    return (transaction.assets ?? []).flatMap((asset) => {
      const details = assetDetailsById.get(asset.assetId);
      const baseItem = {
        lin: asset.lin === '—' ? null : asset.lin,
        nsn: asset.nsn === '—' ? null : asset.nsn,
        nomenclature: asset.nomenclature || '—',
        size: details?.size ?? null,
        condition: details?.condition ?? null,
        quantity: asset.transQty ?? 1,
        date,
        individual,
      };

      // For exchange transactions, create two rows: one for turn-in, one for issue
      if (isExchange) {
        return [
          {
            ...baseItem,
            turnInOrIssue: 'DX-TI',
            condition: turnInCondition,
          } satisfies DxReportItem,
          {
            ...baseItem,
            turnInOrIssue: 'DX-I',
            condition: 'S',
          } satisfies DxReportItem,
        ];
      }

      // For regular turn-in or issue, create single row
      return [{
        ...baseItem,
        turnInOrIssue: baseDirection,
      } satisfies DxReportItem];
    });
  });

  const options: DxReportTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
    },
    items,
  };

  const html = buildDxReportHtml(options);
  outputReport(html, 'dx-report.html', getOutputFormat(params));
};
